import { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Bell,
  LogOut,
  Menu,
  Settings,
  User,
  LockKeyhole,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NotificationDrawer } from '@/components/ui/NotificationDrawer';
import { NotificationsPanel } from '@/components/ui/NotificationsPanel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useFinancialYear } from '@/hooks/useFinancialYear';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { clearStoredSession } from '@/lib/session/sessionManager';
import { useLogoutMutation } from '@/services/authService';
import { useGetUserNotificationsQuery } from '@/services/notificationsService';
import { resetAll } from '@/store/actions/resetActions';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toggleMobileSidebar, toggleSidebar } from '@/store/slices/uiSlice';
import { clearSession, hydrateSession } from '@/store/slices/userSlice';
import { ChangePasswordDialog } from './auth/ChangePasswordDialog';
import { toast } from '../ui/toast';
import { getFinancialYear } from '@/lib/utils/dateUtils';

export function Header() {
  const [isChangePwdOpen, setIsChangePwdOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { navigateSafe } = useSafeNavigate();
  const { session, preferences } = useAppSelector((state) => state.user);
  const { sidebarCollapsed } = useAppSelector((state) => state.ui);
  const [isSearchFocused] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  const [logout] = useLogoutMutation();

  // Get user data from Redux session state
  const userData = session.user;
  const notificationsRef = useRef<HTMLDivElement>(null);

  // ================================================================
  // CLICK OUTSIDE HANDLER
  // ================================================================
  //
  // Close notifications panel when clicking outside
  // ================================================================
  useClickOutside(notificationsRef, () => {
    setIsNotificationsOpen(false);
  });

  // ================================================================
  // NOTIFICATION HANDLERS
  // ================================================================
  //
  // Handle opening drawer from panel "View All" button
  // ================================================================
  const handleOpenNotificationDrawer = () => {
    setIsNotificationsOpen(false); // Close panel
    setIsNotificationDrawerOpen(true); // Open drawer
  };

  // ================================================================
  // REAL-TIME NOTIFICATIONS
  // ================================================================
  //
  // Socket connection for real-time notifications
  // Manages connection state and notification popups
  // ================================================================

  const { unreadCount: socketUnreadCount } = useNotificationSocket({
    token: sessionStorage.getItem('authToken') || '',
    uid: userData?.branch_code || '',
    enabled: !!userData?.branch_code && userData?.branch_code !== 'ADMIN', // Disable for ADMIN users
  });

  // ================================================================
  // NOTIFICATIONS DATA
  // ================================================================
  //
  // Fetch unread notifications count for badge display
  // Use minimal query to get just the count efficiently
  // Combine with socket unread count for real-time updates
  // No polling needed since socket provides real-time updates
  // ================================================================

  const { data: notificationsData } = useGetUserNotificationsQuery(
    {
      uid: userData?.branch_code || '',
      page: 1,
      limit: 1,
      status: 'All',
    },
    {
      skip: !userData?.branch_code || userData?.branch_code === 'ADMIN', // Skip for ADMIN users
    },
  );

  // Use the maximum of API unread count and socket unread count
  // This ensures we show the most up-to-date count
  const apiUnreadCount = notificationsData?.data?.pagination?.unread_count || 0;
  const unreadCount = Math.max(apiUnreadCount, socketUnreadCount);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      // 1. Purge storage first to remove tokens before state reset
      clearStoredSession();
      // 2. Clear domain session state (explicit semantic action)
      dispatch(clearSession());
      // 3. Global state reset (meta-reducer will reinitialize all slices)
      dispatch(resetAll());
      // 4. Mark hydration complete so routing guards don't hang on isHydrated = false
      dispatch(hydrateSession({}));

      navigateSafe('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Ensure cleanup even if API call fails
      clearStoredSession();
      dispatch(clearSession());
      dispatch(resetAll());
      dispatch(hydrateSession({}));

      navigateSafe('/login');
    }
  };

  const getInitials = (name: string) => {
    return (
      name
        ?.split(' ')
        ?.map((word) => word.charAt(0).toUpperCase())
        ?.slice(0, 2)
        ?.join('') ?? ''
    );
  };

  // ================================================================
  // FINANCIAL YEAR INTEGRATION - FIXED!
  // ================================================================
  //
  // Problem: Header was using local state, not connected to Redux
  // Solution: Use Redux-based useFinancialYear hook
  // Now: Changes in header will update Redux → trigger position page updates
  // ================================================================

  const { selectedFinancialYear, availableFinancialYears, changeFinancialYear } =
    useFinancialYear();

  // Debug logging for header financial year changes
  const location = useLocation();
  const isDashboardPage =
    location.pathname.toLowerCase().split('/').filter(Boolean).pop() === 'dashboard';

  // ================================================================
  // AUTO-RESET FINANCIAL YEAR ON DASHBOARD NAVIGATION
  // ================================================================
  //
  // Purpose: Automatically set financial year to current year when user navigates to dashboard
  // Trigger: Runs whenever location.pathname changes
  // Logic: Check if on dashboard page, if yes and year is not current, auto-reset to current year
  // ================================================================

  useEffect(() => {
    if (isDashboardPage) {
      const currentYear = String(getFinancialYear());
      if (selectedFinancialYear !== currentYear) {
        changeFinancialYear(`${currentYear}-${String(parseInt(currentYear, 10) + 1)}`);
      }
    }
  }, [location.pathname, isDashboardPage, selectedFinancialYear, changeFinancialYear]);

  const handleFinancialYearChange = (newYear: string) => {
    const path = location.pathname.toLowerCase();
    const lastSegment = path?.split('/')?.filter(Boolean)?.pop();
    if (lastSegment === 'dashboard') {
      // Reset to current financial year and show toast
      // Ensure we work with strings so changeFinancialYear receives a string
      const currentYearStr = String(getFinancialYear());
      const nextYearStr = String(parseInt(currentYearStr, 10) + 1);
      changeFinancialYear(`${currentYearStr}-${nextYearStr}`);
      toast({
        title: 'Action Restricted',
        description: 'Dashboard access is restricted to the current financial year only.',
        variant: 'default',
      });
      return; // Block change on dashboard route
    }
    // Debug log (retain or remove as needed)

    changeFinancialYear(newYear);
  };

  return (
    <motion.header
      initial={{ y: -60 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="z-20 flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 shadow-sm lg:px-6"
    >
      {/* Left section */}
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl transition-all duration-200 hover:bg-[#118AB2]/10 lg:hidden"
          onClick={() => dispatch(toggleMobileSidebar())}
        >
          <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
            <Menu className="h-5 w-5 text-gray-600" />
          </motion.div>
        </Button>

        {/* Sidebar collapse/expand (desktop only) */}
        <div className="hidden lg:block">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dispatch(toggleSidebar())}
            className="rounded-xl px-3 transition-all duration-200 hover:bg-[#118AB2]/10"
          >
            {sidebarCollapsed ? (
              <div className="flex items-center gap-2 text-gray-700">
                <ChevronRight className="h-4 w-4" />
              </div>
            ) : (
              <div className="flex items-center gap-2 text-gray-700">
                <ChevronLeft className="h-4 w-4" />
              </div>
            )}
          </Button>
        </div>

        {/* Page title with breadcrumb styling */}
        {/* <div className="hidden sm:block">
          <motion.h1
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-2 text-xl font-bold text-gray-900"
          >
            <span className="text-[#118AB2]">/</span>
            <span className="bg-gradient-to-r from-[#118AB2] to-[#06D6A0] bg-clip-text text-transparent">
              {pageTitle}
            </span>
          </motion.h1>
        </div> */}
      </div>

      {/* Center section - Enhanced search */}
      <div className="mx-4 max-w-xl flex-1">
        <motion.div
          className="relative"
          animate={{
            scale: 1,
          }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        >
          {/* <Search
            className={cn(
              'absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform transition-colors duration-200',
              isSearchFocused ? 'text-[#118AB2]' : 'text-gray-400',
            )}
          /> */}
          {/* <Input
            placeholder="Search clients, transactions, reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            className="rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 transition-all duration-200 focus:border-[#118AB2] focus:ring-1 focus:ring-[#118AB2]"
          /> */}

          {/* Search suggestions (when focused and has query) */}
          <AnimatePresence>
            {isSearchFocused && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute left-0 right-0 top-full z-50 mt-2 rounded-xl border border-gray-200 bg-white p-2 shadow-xl backdrop-blur-xl"
              >
                <div className="p-2 text-sm text-gray-500">Quick search results...</div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Right section with better spacing */}
      <div className="flex items-center gap-2">
        {/* Year Picker - FIXED: Now uses Redux state! - Made visible on mobile */}
        <div className="flex min-w-[120px] sm:min-w-[140px]">
          <Select
            value={selectedFinancialYear}
            onValueChange={handleFinancialYearChange}
            // disabled={isDashboardPage}
          >
            <SelectTrigger className="w-[120px] rounded-xl border border-gray-200 bg-gray-50 transition-all duration-200 hover:border-[#118AB2] focus:border-[#118AB2] focus:ring-1 focus:ring-[#118AB2] disabled:cursor-not-allowed disabled:opacity-60 sm:w-[140px]">
              <SelectValue placeholder="Select Year" />
            </SelectTrigger>
            <SelectContent className="rounded-xl border border-gray-200 bg-white shadow-xl">
              {availableFinancialYears?.map((option) => (
                <SelectItem
                  key={option}
                  value={option}
                  className="hover:bg-[#118AB2]/10 focus:bg-[#118AB2]/10"
                >
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Notifications - Hide for ADMIN users */}
        {userData?.branch_code !== 'ADMIN' && (
          <div className="relative" ref={notificationsRef}>
            <motion.div whileTap={{ scale: 0.95 }}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className="relative rounded-lg text-muted-foreground transition-all duration-200 hover:bg-accent hover:text-foreground"
              >
                <motion.div
                  animate={
                    unreadCount > 0
                      ? {
                          rotate: [0, -10, 10, -10, 0],
                          transition: { duration: 0.5, repeat: Infinity, repeatDelay: 3 },
                        }
                      : {}
                  }
                >
                  <Bell className="h-5 w-5" />
                </motion.div>
                {unreadCount > 0 && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -right-1 -top-1"
                  >
                    <Badge
                      variant="default"
                      className="flex h-5 w-5 items-center justify-center p-0 text-[9px]"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  </motion.div>
                )}
              </Button>
            </motion.div>

            {/* Notifications Panel - Dropdown style */}
            <AnimatePresence>
              {isNotificationsOpen && (
                <div className="absolute right-0 top-full z-50 mt-2">
                  <NotificationsPanel
                    uid={userData?.branch_code || ''}
                    onClose={() => setIsNotificationsOpen(false)}
                    onViewAllClick={handleOpenNotificationDrawer}
                  />
                </div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Notification Drawer - Hide for ADMIN users */}
        {userData?.branch_code !== 'ADMIN' && (
          <NotificationDrawer
            isOpen={isNotificationDrawerOpen}
            onOpenChange={setIsNotificationDrawerOpen}
            uid={userData?.branch_code || ''}
          />
        )}

        {/* User menu - Enhanced with design system colors */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <motion.div whileTap={{ scale: 0.95 }}>
              <Button
                variant="ghost"
                className="relative h-10 w-10 rounded-full transition-all duration-200 hover:bg-accent"
              >
                <Avatar className="h-9 w-9 border-2 border-border shadow-sm">
                  <AvatarImage src={userData?.avatar} alt={userData?.name} />
                  <AvatarFallback className="bg-gradient-to-r from-[#118AB2] to-[#06D6A0] text-sm font-bold text-white">
                    {userData?.name ? getInitials(userData.name) : 'U'}
                  </AvatarFallback>
                </Avatar>
                {/* Online status indicator */}
                <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background bg-green-500 shadow-sm"></div>
              </Button>
            </motion.div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-64 rounded-lg border bg-card shadow-lg backdrop-blur-xl"
            align="end"
            forceMount
            sideOffset={8}
          >
            <DropdownMenuLabel className="p-4 font-normal">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={userData?.avatar} alt={userData?.name} />
                    <AvatarFallback className="bg-gradient-to-r from-[#118AB2] to-[#06D6A0] font-semibold text-white">
                      {userData?.name ? getInitials(userData.name) : 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-semibold leading-none text-foreground">
                      {userData?.name || userData?.branch_code}
                    </p>
                    <p className="mt-1 text-xs leading-none text-muted-foreground">
                      {userData?.email || `Branch: ${userData?.branch_code}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="secondary"
                    className="border-[#118AB2]/20 bg-[#118AB2]/10 text-xs text-[#118AB2]"
                  >
                    {userData?.user_type}
                  </Badge>
                  <p className="mt-1 text-xs leading-none text-muted-foreground">
                    {`User Type: ${userData?.user_type}`}
                  </p>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {/* Change Password - visible only for user_type self */}
            {userData?.user_type === 'self' && (
              <DropdownMenuItem
                onClick={() => setIsChangePwdOpen(true)}
                className="mx-2 rounded-lg p-3 transition-colors hover:bg-accent"
              >
                <LockKeyhole className="mr-3 h-4 w-4 text-primary" />
                <span>Change Password</span>
              </DropdownMenuItem>
            )}
            {/* Hide profile tab for ADMIN users */}
            {userData?.branch_code !== 'ADMIN' && (
              <DropdownMenuItem
                onClick={() => navigateSafe('/profile')}
                className="mx-2 rounded-lg p-3 transition-colors hover:bg-accent"
              >
                <User className="mr-3 h-4 w-4 text-primary" />
                <span>Profile</span>
              </DropdownMenuItem>
            )}
            {/* <DropdownMenuItem
              onClick={() => navigateSafe('/settings')}
              className="mx-2 rounded-lg p-3 transition-colors hover:bg-accent"
            >
              <Settings className="mr-3 h-4 w-4 text-primary" />
              <span>Settings</span>
            </DropdownMenuItem> */}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="mx-2 rounded-lg p-3 text-destructive transition-colors hover:bg-destructive/10"
            >
              <LogOut className="mr-3 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Change Password Modal (Formik + Yup) */}
        <ChangePasswordDialog
          open={isChangePwdOpen}
          onOpenChange={setIsChangePwdOpen}
          userType={userData?.user_type === 'team' ? 'team' : 'self'}
        />
      </div>
    </motion.header>
  );
}
